/**
 * @file The option picker inside a Home column menu, rendered by
 * `DocListColumnMenu` under its `filter-select` item.
 *
 * On the Subscribed, Assigned, Modified by and Type columns an option's value is
 * a document id while its label is a person's name or a form type's short label.
 * rc-select re-filters whatever options it is handed and matches the value
 * unless `optionFilterProp` says otherwise, so a filtering pass placed outside
 * the `Select` is discarded by it and the search box comes back empty.
 *
 * Option order belongs to the producer. `DocListTableHeader` builds the map in
 * the order the options should render and this component never reorders it. The
 * exception is the `Me`-first pass on the user columns, which sorts by label
 * and, being a stable sort, leaves ties in the producer's order.
 *
 * The popup is capped to the viewport less `marginLG` per side, so a wide
 * `filterDropdownWidth` still fits a phone.
 */
import { Select, theme } from 'antd';
import { useMemo, useCallback } from 'react';
import { INCLUDE_BLANKS_VALUE, REMOVE_BLANKS_VALUE } from '../constants';

interface ColumnFilterProps {
  columnId: string;
  filterValue: unknown;
  onFilterChange: (value: unknown) => void;
  onFilterClear: () => void;
  facetedUniqueValues: Map<string, number>;
  columnWidth: number;
  filterDropdownWidth?: number;
  getLabel?: (columnId: string, value: unknown) => string;
  ariaLabel?: string;
}

function ColumnFilter({
  columnId,
  filterValue,
  onFilterChange,
  onFilterClear,
  facetedUniqueValues,
  columnWidth,
  filterDropdownWidth,
  getLabel,
  ariaLabel,
}: ColumnFilterProps) {
  const { token } = theme.useToken();

  const columnFilterValue = Array.isArray(filterValue)
    ? filterValue.map((value) => String(value))
    : filterValue === null || filterValue === undefined || String(filterValue) === ''
      ? undefined
      : [String(filterValue)];

  const dropdownWidth = filterDropdownWidth ?? Math.max(columnWidth, 200);

  const isUserIdColumn =
    columnId === 'assignedTo' || columnId === 'subscribed' || columnId === 'modifiedBy';

  const hasBlankValues = Array.from(facetedUniqueValues.keys()).some(
    (value) => value === '' || value === null || value === undefined ||
      (Array.isArray(value) && value.length === 0)
  );

  const uniqueValues = Array.from(facetedUniqueValues.keys())
    .filter((value) => value !== '' && value !== null && value !== undefined);

  const getLabelText = useCallback((value: unknown): string => {
    const strValue = String(value);
    if (getLabel) {
      const labeled = getLabel(columnId, strValue);
      return labeled || strValue;
    }
    return strValue;
  }, [getLabel, columnId]);

  const baseOptions = useMemo(() => uniqueValues.map((value) => ({
    label: getLabelText(value),
    value: String(value),
  })), [uniqueValues, getLabelText]);

  const allNonBlankValues = useMemo(() => baseOptions.map(opt => opt.value), [baseOptions]);

  const displayValue = columnFilterValue ?? [];

  const handleChange = (value: string[]) => {
    if (value.includes(REMOVE_BLANKS_VALUE)) {
      const withoutSpecial = value.filter(v => v !== REMOVE_BLANKS_VALUE && v !== INCLUDE_BLANKS_VALUE);
      const newValue = [...new Set([...withoutSpecial, ...allNonBlankValues])];
      onFilterChange(newValue.length > 0 ? newValue : undefined);
      return;
    }

    onFilterChange(value.length > 0 ? value : undefined);
  };

  const handleClear = () => {
    onFilterClear();
  };

  const options = useMemo(() => {
    if (baseOptions.length === 0) {
      return [];
    }
    const selectAllOption = hasBlankValues ? [{
      label: 'Select All (no blanks)',
      value: REMOVE_BLANKS_VALUE,
    }] : [];

    const blanksOption = hasBlankValues ? [{
      label: '(Blanks)',
      value: INCLUDE_BLANKS_VALUE,
    }] : [];

    return isUserIdColumn
      ? [
          ...selectAllOption,
          ...baseOptions
            .slice()
            .sort((a, b) => {
              const aIsMe = a.label === 'Me';
              const bIsMe = b.label === 'Me';

              if (aIsMe && !bIsMe) return -1;
              if (!aIsMe && bIsMe) return 1;

              return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
            }),
          ...blanksOption,
        ]
      : [...selectAllOption, ...baseOptions, ...blanksOption];
  }, [baseOptions, isUserIdColumn, hasBlankValues]);

  if (uniqueValues.length === 0) {
    return null;
  }

  return (
    <Select
      mode="multiple"
      allowClear
      placeholder="All"
      value={displayValue}
      onChange={handleChange}
      onClear={handleClear}
      style={{
        width: '100%',
        fontSize: token.fontSizeSM,
      }}
      size="small"
      maxTagCount="responsive"
      popupMatchSelectWidth={dropdownWidth}
      styles={{
        popup: {
          root: { maxWidth: `calc(100vw - ${token.marginLG * 2}px)` },
        },
      }}
      options={options}
      showSearch={{
        optionFilterProp: 'label',
      }}
      aria-label={ariaLabel || `doclist-filter-select-${columnId}`}
    />
  );
}

export default ColumnFilter;
